/**
 * CourtierClientsTab - Onglet Clients du portail courtier.
 *
 * Liste les clients (leads) sur lesquels Alexandre a ete invite (avant ou apres
 * attribution d'un projet) + permet de telecharger les pieces uploadees par le
 * client via le proxy /api/courtier/documents/{file_id}/download.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null || n === '') return '—';
  return Math.round(n).toLocaleString('fr-FR') + ' EUR';
};

const CourtierClientsTab = () => {
  const [clients, setClients] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [verdictDraft, setVerdictDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteFeedback, setNoteFeedback] = useState(null);

  const loadClients = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/courtier/clients`);
      setClients(r.data || []);
      if ((r.data || []).length === 1) {
        setSelectedId(r.data[0].lead_id);
      }
    } catch {
      setClients([]);
    } finally { setLoadingList(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadDetail = useCallback(async (lid) => {
    if (!lid) return;
    setLoadingDetail(true);
    try {
      const [d, docResp] = await Promise.all([
        axios.get(`${API}/courtier/clients/${lid}`),
        axios.get(`${API}/courtier/clients/${lid}/documents`),
      ]);
      setDetail(d.data);
      setDocs(docResp.data);
      setNoteDraft((d.data?.courtier_note?.text) || '');
      setVerdictDraft((d.data?.courtier_note?.verdict) || '');
    } catch {
      setDetail(null);
      setDocs(null);
      setNoteDraft('');
      setVerdictDraft('');
    } finally { setLoadingDetail(false); }
  }, []);

  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);

  // Sync verdictDraft and noteDraft chaque fois que detail.courtier_note change
  // (defensif : le testing agent a observe un cas ou la chip n'etait pas highlighted apres reload)
  useEffect(() => {
    if (detail) {
      setNoteDraft(detail.courtier_note?.text || '');
      setVerdictDraft(detail.courtier_note?.verdict || '');
    }
  }, [detail?.lead_id, detail?.courtier_note?.verdict, detail?.courtier_note?.text, detail]);

  const saveNote = async () => {
    if (!selectedId) return;
    setSavingNote(true);
    setNoteFeedback(null);
    try {
      const r = await axios.put(`${API}/courtier/clients/${selectedId}/note`, {
        note: noteDraft,
        verdict: verdictDraft || null,
      });
      setDetail({ ...detail, courtier_note: r.data.courtier_note });
      setNoteFeedback({ type: 'success', msg: 'Verdict enregistre. L\'equipe RealEase est notifiee.' });
    } catch (e) {
      setNoteFeedback({ type: 'error', msg: e.response?.data?.detail || 'Echec enregistrement' });
    } finally { setSavingNote(false); }
  };

  const downloadFile = async (file) => {
    setDownloadingId(file.file_id);
    try {
      const resp = await axios.get(`${API}/courtier/documents/${file.file_id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || `document-${file.file_id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      alert(e.response?.data?.detail || 'Telechargement impossible');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="courtier-clients-tab">
      {/* Sidebar : liste clients */}
      <aside className="lg:col-span-3" data-testid="courtier-clients-list">
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Mes clients</h2>
        {loadingList ? (
          <div className="text-sm text-gray-400 italic">Chargement...</div>
        ) : clients.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-500">
              Aucun client ne vous a encore ete partage par l'equipe RealEase.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {clients.map((c) => (
              <button
                key={c.lead_id}
                type="button"
                onClick={() => setSelectedId(c.lead_id)}
                className={`w-full text-left p-3 rounded-lg border transition ${
                  selectedId === c.lead_id
                    ? 'bg-[#13736D] text-white border-[#13736D]'
                    : 'bg-white border-gray-200 hover:border-[#13736D]'
                }`}
                data-testid={`courtier-client-item-${c.lead_id}`}
              >
                <p className="font-semibold text-sm truncate">
                  {(c.last_name || '').toUpperCase()} {c.first_name || ''}
                </p>
                {c.email && (
                  <p className={`text-xs mt-0.5 truncate ${selectedId === c.lead_id ? 'text-white/80' : 'text-gray-500'}`}>
                    {c.email}
                  </p>
                )}
                {c.city && (
                  <p className={`text-xs ${selectedId === c.lead_id ? 'text-white/70' : 'text-gray-400'}`}>
                    {c.city}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </aside>

      {/* Detail */}
      <main className="lg:col-span-9 space-y-6">
        {!selectedId ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p>Selectionnez un client dans la liste pour voir le detail.</p>
          </div>
        ) : loadingDetail || !detail ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-400">
            Chargement du dossier client...
          </div>
        ) : (
          <>
            {/* Identite */}
            <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-detail-header">
              <h1 className="text-2xl font-bold text-gray-900">
                {(detail.last_name || '').toUpperCase()} {detail.first_name || ''}
              </h1>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                {detail.email && <span>{detail.email}</span>}
                {detail.phone && <span>{detail.phone}</span>}
                {detail.city && <span>{detail.city}{detail.postal_code ? ` (${detail.postal_code})` : ''}</span>}
              </div>
              {detail.address && <p className="text-xs text-gray-500 mt-1">{detail.address}</p>}
              {detail.linked_project_id && (
                <p className="text-xs text-[#13736D] mt-2 font-semibold">
                  Projet en cours : <code className="bg-gray-100 px-1.5 py-0.5 rounded">{detail.linked_project_id}</code>
                </p>
              )}
            </div>

            {/* Co-investisseurs */}
            {detail.co_investors && detail.co_investors.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-coinvestors">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Co-investisseur{detail.co_investors.length > 1 ? 's' : ''} ({detail.co_investors.length})
                </h2>
                <div className="space-y-2">
                  {detail.co_investors.map((co, idx) => (
                    <div key={idx} className="p-2 rounded-md bg-gray-50 text-sm">
                      <p className="font-semibold text-gray-900">
                        {(co.last_name || '').toUpperCase()} {co.first_name || ''}
                      </p>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                        {co.email && <span>{co.email}</span>}
                        {co.phone && <span>{co.phone}</span>}
                        {co.situation_pro && <span>{co.situation_pro}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Situation financiere */}
            <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-finance">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Situation financiere</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <div className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-600">Revenu mensuel net</span>
                  <span className="font-semibold text-gray-900">{fmt(detail.finance?.revenu_mensuel_net)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-600">Apport disponible</span>
                  <span className="font-semibold text-[#13736D]">{fmt(detail.finance?.apport_disponible)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-600">Endettement actuel</span>
                  <span className="font-semibold text-gray-900">{fmt(detail.finance?.endettement_actuel)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-600">Situation pro</span>
                  <span className="font-semibold text-gray-900">{detail.finance?.situation_pro || '—'}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-600">Situation familiale</span>
                  <span className="font-semibold text-gray-900">{detail.finance?.situation_familiale || '—'}</span>
                </div>
              </div>
              {detail.finance?.credits_en_cours && detail.finance.credits_en_cours.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Credits en cours</p>
                  <ul className="text-xs space-y-1">
                    {detail.finance.credits_en_cours.map((cr, idx) => (
                      <li key={idx} className="flex justify-between bg-gray-50 rounded px-2 py-1">
                        <span>{cr.type || cr.label || 'Credit'} {cr.organisme ? `(${cr.organisme})` : ''}</span>
                        <span className="font-semibold">{fmt(cr.mensualite || cr.amount)} / mois</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Recherche projet */}
            {detail.project_search && (detail.project_search.budget_max || (detail.project_search.geographic_zones?.length || 0) > 0) && (
              <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-project-search">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Recherche projet</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Budget min</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.project_search?.budget_min)}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-100 py-1">
                    <span className="text-gray-600">Budget max</span>
                    <span className="font-semibold text-gray-900">{fmt(detail.project_search?.budget_max)}</span>
                  </div>
                </div>
                {detail.project_search?.geographic_zones && detail.project_search.geographic_zones.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    <b>Zones :</b> {detail.project_search.geographic_zones.join(', ')}
                  </p>
                )}
                {detail.project_search?.objectifs && (
                  <p className="text-xs text-gray-600 mt-1"><b>Objectifs :</b> {detail.project_search.objectifs}</p>
                )}
              </div>
            )}

            {/* Note + verdict courtier (privee, notifiee a l'equipe) */}
            <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-note-panel">
              <div className="mb-2">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#A57F4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Mon analyse / verdict
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  L'equipe RealEase est notifiee a chaque mise a jour.
                  {detail.courtier_note?.updated_at && (
                    <span className="ml-2 text-gray-400">
                      Mise a jour : {new Date(detail.courtier_note.updated_at).toLocaleString('fr-FR')}
                    </span>
                  )}
                </p>
              </div>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Ex: Capacite d'emprunt estimee a 280k sur 25 ans. Besoin du dernier bulletin pour finaliser."
                rows={4}
                className="w-full text-sm border border-gray-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-[#13736D]/30 focus:border-[#13736D]"
                data-testid="courtier-client-note-textarea"
              />
              <div className="mt-3">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Verdict</p>
                <div className="flex flex-wrap gap-2" data-testid="courtier-client-verdict-chips">
                  {[
                    { v: 'favorable',   label: 'Favorable',   color: 'emerald' },
                    { v: 'a_etudier',   label: 'A etudier',   color: 'amber'   },
                    { v: 'defavorable', label: 'Defavorable', color: 'red'     },
                  ].map(o => {
                    const active = verdictDraft === o.v;
                    const colorMap = {
                      emerald: active ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50',
                      amber:   active ? 'bg-amber-500 border-amber-500 text-white'     : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-50',
                      red:     active ? 'bg-red-600 border-red-600 text-white'         : 'bg-white border-red-300 text-red-700 hover:bg-red-50',
                    };
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setVerdictDraft(active ? '' : o.v)}
                        className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${colorMap[o.color]}`}
                        data-testid={`courtier-client-verdict-${o.v}`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                {noteFeedback && (
                  <p className={`text-xs ${noteFeedback.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`} data-testid="courtier-client-note-feedback">
                    {noteFeedback.msg}
                  </p>
                )}
                <button
                  type="button"
                  onClick={saveNote}
                  disabled={savingNote}
                  className="ml-auto px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
                  data-testid="courtier-client-note-save"
                >
                  {savingNote ? 'Enregistrement...' : 'Enregistrer mon verdict'}
                </button>
              </div>
            </div>

            {/* Documents Drive */}
            <div className="bg-white rounded-lg border border-gray-200 p-5" data-testid="courtier-client-docs">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Pieces justificatives ({docs?.files?.length || 0})
              </h2>
              {(!docs || (docs.files || []).length === 0) ? (
                <p className="text-sm text-gray-400 italic">
                  Aucune piece n'a encore ete deposee par le client.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {(docs.files || []).map((f) => (
                    <div
                      key={f.file_id}
                      className="flex items-center gap-3 p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition text-sm"
                      data-testid={`courtier-client-doc-${f.file_id}`}
                    >
                      <svg className="w-4 h-4 text-[#13736D] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{f.filename}</p>
                        <p className="text-xs text-gray-500">{f.folder}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${
                        f.source === 'project' ? 'bg-[#A57F4F]/10 text-[#A57F4F]' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {f.source === 'project' ? 'Projet' : 'Client'}
                      </span>
                      <button
                        type="button"
                        onClick={() => downloadFile(f)}
                        disabled={downloadingId === f.file_id}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition shrink-0"
                        data-testid={`courtier-client-download-${f.file_id}`}
                      >
                        {downloadingId === f.file_id ? '...' : 'Telecharger'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default CourtierClientsTab;
